import {
  AnalysisByteHour,
  AnalysisByteSpend,
  PackageAnalysis,
  ProjectAnalysis,
  ReportAnalysis,
  SocialCommitmentAnalysis,
} from "@entities/analysis";
import { getImageUrl } from "@hooks/utils/useUpload";
import {
  AnalysisByteHourLineItem,
  AnalysisByteSpendLineItem,
  PackageAnalysisLineItem,
  ProjectAnalysisLineItem,
  ReportAnalysisLineItem,
  SocialCommitmentAnalysisLineItem,
} from "@interfaces/analysis";
import { decodeReportStatus } from "./contractorReport";

export const decodeProjectAnalysis = async (
  projectAnalysis: ProjectAnalysis
): Promise<ProjectAnalysisLineItem> => {
  const presignedUrl =
    projectAnalysis.contractor_logo.length > 0
      ? await getImageUrl(projectAnalysis.contractor_logo)
      : "";
  const packages = await Promise.all(
    projectAnalysis.packages.map((p) => decodePackageAnalysis(p))
  );
  return {
    projectId: projectAnalysis.project_id,
    projectName: projectAnalysis.project_name,
    contractorId: projectAnalysis.contractor_id,
    contractorName: projectAnalysis.contractor_name,
    contractorLogo: presignedUrl,
    projectStartDate: new Date(projectAnalysis.project_start_date),
    projectEndDate: new Date(projectAnalysis.project_end_date),
    targets: projectAnalysis.targets.map((t) =>
      decodeSocialCommitmentAnalysis(t)
    ),
    totalSpendTarget: projectAnalysis.total_spend_target,
    totalSpendAchieved: projectAnalysis.total_spend_achieved,
    totalHoursTarget: projectAnalysis.total_hours_target,
    totalHoursAchieved: projectAnalysis.total_hours_achieved,
    packages,
  } as ProjectAnalysisLineItem;
};

export const decodePackageAnalysis = async (
  packageAnalysis: PackageAnalysis
): Promise<PackageAnalysisLineItem> => {
  const presignedUrl =
    packageAnalysis.contractor_logo.length > 0
      ? await getImageUrl(packageAnalysis.contractor_logo)
      : "";
  return {
    contractorPackageId: packageAnalysis.contractor_package_id,
    packageStartDate: new Date(packageAnalysis.package_start_date),
    packageEndDate: new Date(packageAnalysis.package_end_date),

    contractorId: packageAnalysis.contractor_id,
    contractorLogo: presignedUrl,
    contractorName: packageAnalysis.contractor_name,

    reportStatus: decodeReportStatus(packageAnalysis.report_status),
    reportData: packageAnalysis.report_data.map((r) => decodeReportAnalysis(r)),
    targets: packageAnalysis.targets.map((t) =>
      decodeSocialCommitmentAnalysis(t)
    ),

    totalSpendTarget: 0,
    totalSpendAchieved: 0,

    totalHoursTarget: 0,
    totalHoursAchieved: 0,
  } as PackageAnalysisLineItem;
};

export const decodeReportAnalysis = (
  reportAnalysis: ReportAnalysis
): ReportAnalysisLineItem => {
  return {
    reportId: reportAnalysis.report_id,
    reportStatus: reportAnalysis.report_status,
    reportDateMonth: reportAnalysis.report_date_month,
    reportDateYear: reportAnalysis.report_date_year,
    targets: reportAnalysis.targets.map((t) =>
      decodeSocialCommitmentAnalysis(t)
    ),
    spendData: reportAnalysis.spend_data.map((s) =>
      decodeReportAnalysisByteSpend(s)
    ),
    hourData: reportAnalysis.hour_data.map((h) =>
      decodeReportAnalysisByteHour(h)
    ),
  } as ReportAnalysisLineItem;
};

export const decodeReportAnalysisByteSpend = (
  analysisByte: AnalysisByteSpend
): AnalysisByteSpendLineItem => {
  return {
    contractorReportId: analysisByte.contractor_report_id,
    socialProcurementId: analysisByte.social_procurement_id,
    targetIds: analysisByte.target_ids,
    value: analysisByte.value,
    prevValue: analysisByte.prev_value,
  } as AnalysisByteSpendLineItem;
};

export const decodeReportAnalysisByteHour = (
  analysisByte: AnalysisByteHour
): AnalysisByteHourLineItem => {
  return {
    contractorReportId: analysisByte.contractor_report_id,
    contractorEmployeeId: analysisByte.contractor_employee_id,
    targetIds: analysisByte.target_ids,
    value: analysisByte.value,
    prevValue: analysisByte.prev_value,
  } as AnalysisByteHourLineItem;
};

export const decodeSocialCommitmentAnalysis = (
  socialCommitment: SocialCommitmentAnalysis
): SocialCommitmentAnalysisLineItem => {
  return {
    id: socialCommitment.id,
    targetType: socialCommitment.target_type,
    targetDescription: socialCommitment.target_description,
    targetValue: socialCommitment.target_value,
    achievedValue: socialCommitment.achieved_value,
    prevAchievedValue: socialCommitment.prev_achieved_value,
  };
};
